import type { HeaderProps } from './types';

/**
 * Универсальный заголовок страниц.
 *
 * Про отступы icon-кнопок в action-слотах:
 * Button с size="icon-header" имеет тач-зону 44×44px (Apple HIG минимум),
 * а иконка внутри — 18×18px. Это даёт 13px "воздуха" между иконкой и краем
 * кнопки. Вместе с px-4 (16px) на хедере иконка визуально стояла бы в 29px
 * от края экрана, что ломает сетку — заголовок и другой контент стоят на 16px.
 *
 * Фикс: оборачиваем сам action в span с отрицательным margin -ml-[13px] / -mr-[13px].
 * Иконка визуально встаёт ровно на 16px от края экрана, но клик-зона кнопки
 * (44×44) сохраняется. Сдвигается только action, заголовок остаётся на своей
 * позиции согласно общему padding хедера.
 */
export function Header({
  title,
  subtitle,
  titleAlign = 'left',
  leftAction,
  rightAction,
  className = ''
}: HeaderProps): JSX.Element {
  const isCenter = titleAlign === 'center';

  return (
    <header className={`sticky top-0 z-10 bg-background px-4 pt-11 pb-2 flex-shrink-0 ${className}`}>
      <div className="relative flex items-center min-h-12">
        {/* Левая часть */}
        <div className="flex items-center gap-4 flex-shrink-0">
          {leftAction && (
            <span className="-ml-[13px] flex items-center">{leftAction}</span>
          )}
          {/* Заголовок слева — сразу после leftAction */}
          {!isCenter && title && (
            <div>
              <h1 className="text-xl font-semibold text-foreground">
                {title}
              </h1>
              {subtitle && (
                <p className="text-base text-muted-foreground mt-1.5">
                  {subtitle}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Заголовок по центру — абсолютное позиционирование */}
        {isCenter && title && (
          <div className="absolute left-1/2 -translate-x-1/2 pointer-events-none">
            <h1 className="text-xl font-semibold text-foreground">
              {title}
            </h1>
            {subtitle && (
              <p className="text-base text-muted-foreground mt-1.5">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {/* Правая часть */}
        <div className="flex items-center gap-0 flex-shrink-0 ml-auto">
          {rightAction && (
            <span className="-mr-[13px] flex items-center">{rightAction}</span>
          )}
        </div>
      </div>
    </header>
  );
}